import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import { ApiFactory } from '../api/easypos-api';
import { resolveAssetUrl } from '../api/asset-url';

// Self-contained client-side image upload + crop tool for a single menu item.
//
// Pipeline:
//   1. <input type="file"> picks a JPEG or PNG.
//   2. The file is loaded into an off-DOM Image and drawn on a square <canvas>
//      preview. The user pans (drag) and zooms (wheel / pinch) to position the
//      crop window.
//   3. On confirm, the visible canvas region is re-rendered to a 600×600
//      offscreen canvas, encoded as JPEG @ 80% quality, and POSTed.
//
// The recommendation copy at the top of the panel pushes users toward a
// roughly square photo around 600px — anything they upload is always cropped
// to a square anyway, so wide/tall sources just need to be repositioned.
@customElement('product-image-editor')
export class MenuImageEditor extends LitElement {
  // The shop the menu item belongs to. Required.
  @property({ type: String, attribute: 'shop-name' }) shopName = '';
  // The menu item id this image is for. Required.
  @property({ type: String, attribute: 'item-id' }) itemId = '';
  // Currently-stored image SAS URL, if any. Used to render the "current image"
  // preview state. Pass null/empty when the item has no image yet.
  @property({ type: String, attribute: 'current-url' }) currentUrl:
    string | null = null;

  // Editing state — once a file is loaded we move to "edit" mode and let the
  // user crop. After upload we go back to "view" mode showing the new image.
  @state() private _editing = false;
  @state() private _busy = false;
  @state() private _error: string | null = null;

  // The HTMLImageElement we're cropping. Loaded from the file the user picked.
  private _src: HTMLImageElement | null = null;

  // Pan + zoom state (in canvas pixels). The crop area is the entire visible
  // canvas — anything that lies inside it is what's saved. Image is rendered at
  // (offset.x, offset.y) scaled by `scale`.
  @state() private _scale = 1;
  @state() private _offset = { x: 0, y: 0 };

  // Pointer drag state.
  private _dragging = false;
  private _dragStart = { x: 0, y: 0 };
  private _dragOffsetStart = { x: 0, y: 0 };

  // The output dimensions — what we save to blob storage and recommend users
  // upload at. 600x600 keeps cards crisp on retina without bloating storage.
  private static readonly Output = 600;

  // The on-screen canvas size — kept ≤ Output so we don't upscale tiny photos
  // beyond their natural resolution in the preview.
  private static readonly Preview = 320;

  static styles = css`
    :host {
      display: block;
    }

    .panel {
      border: 1px solid var(--app-border, var(--sl-color-neutral-200));
      border-radius: var(--sl-border-radius-large);
      padding: 1rem;
      background: var(--app-surface, var(--sl-color-neutral-0));
      display: grid;
      gap: 0.75rem;
    }

    h3 {
      margin: 0;
      font-size: 0.95rem;
      font-weight: 600;
    }

    .hint {
      margin: 0;
      font-size: 0.8rem;
      color: var(--app-text-muted, var(--sl-color-neutral-500));
      line-height: 1.45;
    }

    .stage {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      align-items: flex-start;
    }

    .preview {
      width: 320px;
      height: 320px;
      border-radius: var(--sl-border-radius-medium);
      border: 1px solid var(--app-border, var(--sl-color-neutral-200));
      background: var(--sl-color-neutral-100);
      position: relative;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--app-text-muted, var(--sl-color-neutral-500));
      font-size: 0.85rem;
    }

    .preview img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    canvas {
      width: 100%;
      height: 100%;
      display: block;
      touch-action: none; /* let us handle pinch/drag ourselves */
      cursor: grab;
    }

    canvas:active {
      cursor: grabbing;
    }

    .crop-frame {
      pointer-events: none;
      position: absolute;
      inset: 0;
      box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.25) inset;
    }

    .controls {
      display: grid;
      gap: 0.6rem;
      flex: 1;
      min-width: 220px;
    }

    .actions {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .file-input {
      display: none;
    }

    .zoom-row {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      font-size: 0.85rem;
    }

    .zoom-row input[type='range'] {
      flex: 1;
    }
  `;

  // ---- File pick → start editing ----

  private async _onPickFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!/^image\/(jpeg|png)$/.test(file.type)) {
      this._error = 'Please pick a JPEG or PNG image.';
      input.value = '';
      return;
    }
    this._error = null;
    const url = URL.createObjectURL(file);
    try {
      const img = await loadImage(url);
      this._src = img;
      this._editing = true;
      this._fitImage();
      requestAnimationFrame(() => this._draw());
    } catch {
      this._error = 'Could not load that image.';
    } finally {
      URL.revokeObjectURL(url);
      input.value = '';
    }
  }

  // Fit-to-cover: scale so the image fully covers the square preview, centred.
  private _fitImage() {
    if (!this._src) return;
    const { naturalWidth: w, naturalHeight: h } = this._src;
    const cover = Math.max(
      MenuImageEditor.Preview / w,
      MenuImageEditor.Preview / h
    );
    this._scale = cover;
    this._offset = {
      x: (MenuImageEditor.Preview - w * cover) / 2,
      y: (MenuImageEditor.Preview - h * cover) / 2,
    };
  }

  // ---- Canvas draw ----

  private _draw() {
    const canvas = this.renderRoot.querySelector('canvas');
    if (!canvas || !this._src) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = MenuImageEditor.Preview * dpr;
    canvas.height = MenuImageEditor.Preview * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#222';
    ctx.fillRect(0, 0, MenuImageEditor.Preview, MenuImageEditor.Preview);
    ctx.drawImage(
      this._src,
      this._offset.x,
      this._offset.y,
      this._src.naturalWidth * this._scale,
      this._src.naturalHeight * this._scale
    );
  }

  // ---- Pan + zoom handlers ----

  private _onPointerDown = (e: PointerEvent) => {
    if (!this._src) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    this._dragging = true;
    this._dragStart = { x: e.clientX, y: e.clientY };
    this._dragOffsetStart = { ...this._offset };
  };

  private _onPointerMove = (e: PointerEvent) => {
    if (!this._dragging) return;
    this._offset = {
      x: this._dragOffsetStart.x + (e.clientX - this._dragStart.x),
      y: this._dragOffsetStart.y + (e.clientY - this._dragStart.y),
    };
    this._draw();
  };

  private _onPointerUp = (e: PointerEvent) => {
    this._dragging = false;
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
  };

  private _onWheel = (e: WheelEvent) => {
    if (!this._src) return;
    e.preventDefault();
    // Zoom toward the cursor position.
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const before = this._scale;
    const factor = e.deltaY < 0 ? 1.08 : 1 / 1.08;
    const next = clamp(before * factor, 0.05, 10);
    // Keep the point under the cursor stationary in image space.
    this._offset = {
      x: cx - ((cx - this._offset.x) * next) / before,
      y: cy - ((cy - this._offset.y) * next) / before,
    };
    this._scale = next;
    this._draw();
  };

  private _onZoomSlider(e: Event) {
    if (!this._src) return;
    const target = e.target as HTMLInputElement;
    const next = Number(target.value);
    // Zoom around the centre when using the slider.
    const c = MenuImageEditor.Preview / 2;
    const before = this._scale;
    this._offset = {
      x: c - ((c - this._offset.x) * next) / before,
      y: c - ((c - this._offset.y) * next) / before,
    };
    this._scale = next;
    this._draw();
  }

  // ---- Confirm / upload ----

  private _exportJpegBlob(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (!this._src) return reject(new Error('No image loaded'));
      const out = document.createElement('canvas');
      out.width = MenuImageEditor.Output;
      out.height = MenuImageEditor.Output;
      const ctx = out.getContext('2d');
      if (!ctx) return reject(new Error('Canvas 2D context unavailable'));

      // Map preview-space to output-space: same offset/scale relationship,
      // multiplied by the resolution ratio.
      const ratio = MenuImageEditor.Output / MenuImageEditor.Preview;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, MenuImageEditor.Output, MenuImageEditor.Output);
      ctx.drawImage(
        this._src,
        this._offset.x * ratio,
        this._offset.y * ratio,
        this._src.naturalWidth * this._scale * ratio,
        this._src.naturalHeight * this._scale * ratio
      );
      out.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('toBlob produced null'))),
        'image/jpeg',
        0.8
      );
    });
  }

  private async _onConfirm() {
    if (this._busy || !this._src) return;
    this._busy = true;
    this._error = null;
    try {
      const blob = await this._exportJpegBlob();
      const r = await ApiFactory.authenticated().uploadProductImage(
        this.shopName,
        this.itemId,
        blob,
        'image/jpeg'
      );
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Upload failed.';
        return;
      }
      this.currentUrl = r.data.imageUrl ?? null;
      this._editing = false;
      this._src = null;
      this.dispatchEvent(
        new CustomEvent('image-changed', {
          detail: { imageUrl: this.currentUrl },
        })
      );
    } finally {
      this._busy = false;
    }
  }

  private _onCancel() {
    this._editing = false;
    this._src = null;
    this._error = null;
  }

  private async _onRemove() {
    if (this._busy) return;
    this._busy = true;
    this._error = null;
    try {
      const r = await ApiFactory.authenticated().deleteProductImage(
        this.shopName,
        this.itemId
      );
      if (!r.success) {
        this._error = r.error ?? 'Could not remove image.';
        return;
      }
      this.currentUrl = null;
      this.dispatchEvent(
        new CustomEvent('image-changed', { detail: { imageUrl: null } })
      );
    } finally {
      this._busy = false;
    }
  }

  // ---- Render ----

  render() {
    return html`
      <section class="panel">
        <h3>Menu image</h3>
        <p class="hint">
          Recommended: a square photo, at least 600×600. We resize and crop to a
          600×600 JPEG before uploading, so wider or taller sources just need
          repositioning. Keep it under ~1 MB.
        </p>

        ${this._editing ? this._renderEditor() : this._renderViewer()}
        ${
          this._error
            ? html`<sl-alert variant="danger" open>${this._error}</sl-alert>`
            : nothing
        }
      </section>
    `;
  }

  private _renderViewer() {
    return html`
      <div class="stage">
        <div class="preview">
          ${
            this.currentUrl
              ? html`<img
                  src=${resolveAssetUrl(this.currentUrl) ?? ''}
                  alt="Current menu image"
                />`
              : html`<span>No image yet</span>`
          }
        </div>

        <div class="controls">
          <input
            class="file-input"
            id="image-file"
            type="file"
            accept="image/jpeg,image/png"
            @change=${this._onPickFile}
          />
          <div class="actions">
            <sl-button
              variant="primary"
              @click=${() =>
                (
                  this.renderRoot.querySelector(
                    '#image-file'
                  ) as HTMLInputElement
                )?.click()}
              ?disabled=${this._busy}
            >
              <sl-icon slot="prefix" name="image"></sl-icon>
              ${this.currentUrl ? 'Replace image' : 'Upload image'}
            </sl-button>
            ${
              this.currentUrl
                ? html`
                    <sl-button
                      variant="default"
                      @click=${this._onRemove}
                      ?loading=${this._busy}
                    >
                      <sl-icon slot="prefix" name="trash"></sl-icon>
                      Remove
                    </sl-button>
                  `
                : nothing
            }
          </div>
        </div>
      </div>
    `;
  }

  private _renderEditor() {
    return html`
      <div class="stage">
        <div class="preview">
          <canvas
            @pointerdown=${this._onPointerDown}
            @pointermove=${this._onPointerMove}
            @pointerup=${this._onPointerUp}
            @pointercancel=${this._onPointerUp}
            @wheel=${this._onWheel}
          ></canvas>
          <div class="crop-frame"></div>
        </div>

        <div class="controls">
          <div class="zoom-row">
            <sl-icon name="zoom-out"></sl-icon>
            <input
              type="range"
              min="0.05"
              max="5"
              step="0.01"
              .value=${String(this._scale)}
              @input=${this._onZoomSlider}
            />
            <sl-icon name="zoom-in"></sl-icon>
          </div>
          <p class="hint">
            Drag the image to position it inside the frame. Pinch or scroll to
            zoom. The square area is what gets saved.
          </p>
          <div class="actions">
            <sl-button
              variant="primary"
              ?loading=${this._busy}
              @click=${this._onConfirm}
            >
              <sl-icon slot="prefix" name="check-lg"></sl-icon>
              Save image
            </sl-button>
            <sl-button variant="default" @click=${this._onCancel}>
              Cancel
            </sl-button>
          </div>
        </div>
      </div>
    `;
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = src;
  });
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}
